import { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fetchJobs } from '../../Redux/ActionCreators/jobsAction'
import './createJob.scss'
import './loading.scss'
import AddJobDetails from './AddJobDetails'
import SelectAIFeatures from './SelectAIFeatures'
import SetAutomationRules from './SetAutomationRules'
import InviteCandidates from './InviteCandidates'
import { useNavigate } from 'react-router-dom'

const STAGES = [
    { step: 1, label: 'Add job details' },
    { step: 2, label: 'Select AI Features' },
    { step: 3, label: 'Set Automation Rule' },
    { step: 4, label: 'Invite Candidates' },
]

const ACTIVE_STATUS_ID = "14107328-840b-4ad0-904f-8a2243720f70";

function CreateJob() {
    const [currentStage, setCurrentStage] = useState(1);
    const [jobId, setJobId] = useState(null);
    const [restrictedEditMode, setRestrictedEditMode] = useState(false);

    // Stable UUID for the entire wizard session — generated once on mount
    const flowSessionId = useRef(
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `flow-${Date.now()}-${Math.random().toString(36).slice(2)}`
    ).current;
    const [selectedResume, setSelectedResume] = useState({
        file_name: "resume_human_resources_manager.pdf",
        url: "https://gappeo.blob.core.windows.net/dev-gappeoai/20260314110652_resume_human_resources_manager.pdf",
    });
    const [showBanner, setShowBanner] = useState(true);
    const [transitioning, setTransitioning] = useState(false);
    const [transitionMessage, setTransitionMessage] = useState("");
    const [screeningLocked, setScreeningLocked] = useState(false);
    const [inviteFullscreen, setInviteFullscreen] = useState(false);
    const hasAnyJobs = useSelector((state) => state.jobs?.hasAnyJobs);
    const jobList = useSelector((state) => state.jobs?.jobList || []);
    const loading = useSelector((state) => state.jobs?.loading);
    const prevLoadingRef = useRef(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchJobs(1, 100, ACTIVE_STATUS_ID, "", "", "", "", ""));
    }, [dispatch]);

    useEffect(() => {
        if (currentStage !== 4) setInviteFullscreen(false);
    }, [currentStage]);

    useEffect(() => {
        const handler = (e) => {
            setScreeningLocked(e.detail?.active === true);
            if (e.detail?.active === true) setInviteFullscreen(true);
        };
        window.addEventListener('INVITE_SCREENING_LOCK', handler);
        return () => window.removeEventListener('INVITE_SCREENING_LOCK', handler);
    }, []);

    useEffect(() => {
        const wasLoading = prevLoadingRef.current;
        prevLoadingRef.current = loading;
        if (loading || !wasLoading) return;
        if (jobId) return;
        // Recruiters are never locked into job creation, so treat them as having jobs
        const isRecruiter = (localStorage.getItem("roleName") || "").toUpperCase() === "RECRUITER";
        if (jobList.length > 0 || isRecruiter) {
            dispatch({ type: "SET_HAS_ANY_JOBS", payload: true });
        } else {
            dispatch({ type: "SET_HAS_ANY_JOBS", payload: false });
        }
    }, [loading, jobList.length, jobId, dispatch]);

    const showNoJobsPopup = () => {
        window.dispatchEvent(new CustomEvent("SHOW_NO_JOBS_POPUP"));
    };

    const goNext = (id, skipIncrement = false) => {
        if (id && typeof id === 'string') {
            setJobId(id);
        }
        setTransitioning(false);
        if (!skipIncrement) {
            setCurrentStage((s) => Math.min(s + 1, 4))
        }
    };

    const handleTransitionStart = (message) => {
        setTransitionMessage(message);
        setTransitioning(true);
        setCurrentStage((s) => Math.min(s + 1, 4));
    };

    const handleTransitionError = () => {
        setTransitioning(false);
        setCurrentStage((s) => Math.max(s - 1, 1));
    };

    const goBack = () => {
        if (currentStage === 1) {
            if (hasAnyJobs === false) { showNoJobsPopup(); return; }
            navigate("/jobs")
        } else {
            setCurrentStage((s) => Math.max(s - 1, 1))
        }
    }

    return (
        <>
            <div className="cj-wrapper" style={{backgroundColor : screeningLocked || inviteFullscreen ? 'transparent' : ''}}>
                {/* Top header */}
                {!screeningLocked && !inviteFullscreen && (<div className="cj-header">
                    <button className="cj-header-back" onClick={() => { if (hasAnyJobs === false) { showNoJobsPopup(); return; } navigate("/jobs"); }} aria-label="Back">
                        <img src="/images/leftarrow-black-icon.svg" alt="Back" width={32} height={32} />
                    </button>
                    <div className="cj-header-info">
                        <h2 className="cj-header-title">{restrictedEditMode ? 'Edit Job' : 'Create Job'}</h2>
                        <span className="cj-header-step">
                            {currentStage}/4: {STAGES[currentStage - 1].label}
                        </span>
                    </div>
                </div>)}

                {/* Progress bar */}
                {!screeningLocked && !inviteFullscreen && (
                    <div className="cj-progress-bar">
                        {STAGES.map((stage) => (
                            <div
                                key={stage.step}
                                className={`cj-progress-segment ${currentStage >= stage.step ? 'cj-progress-segment--filled' : ''}`}
                            />
                        ))}
                    </div>
                )}

                {/* Mobile info banner */}
                {showBanner && !screeningLocked && !inviteFullscreen && (
                    <div className='cj-wrapper-sm'>
                        <img src='/images/lightbulb-on.svg' alt='' />
                        <h3>For best experience please use Laptop or desktop to create job</h3>
                        <button className='cj-wrapper-sm-close' onClick={() => setShowBanner(false)} aria-label="Close">&#x2715;</button>
                    </div>
                )}

                {/* Stage content */}
                <div className="cj-content" style={{backgroundColor : screeningLocked || inviteFullscreen ? 'transparent' : ''}}>
                    {currentStage === 1 && <AddJobDetails initialJobId={jobId} onNext={goNext} onTransitionStart={handleTransitionStart} onTransitionError={handleTransitionError} onResumeSelect={setSelectedResume} flowSessionId={flowSessionId} onRestrictedEditChange={setRestrictedEditMode} />}
                    {currentStage === 2 && <SelectAIFeatures jobId={jobId} onNext={goNext} onBack={goBack} transitioning={transitioning} transitionMessage={transitionMessage} onTransitionStart={handleTransitionStart} onTransitionError={handleTransitionError} flowSessionId={flowSessionId} isRestrictedEdit={restrictedEditMode} />}
                    {currentStage === 3 && <SetAutomationRules jobId={jobId} onNext={goNext} onBack={goBack} transitioning={transitioning} transitionMessage={transitionMessage} flowSessionId={flowSessionId} />}
                    {currentStage === 4 && <InviteCandidates jobId={jobId} onBack={goBack} transitioning={transitioning} transitionMessage={transitionMessage} resumeUrl={selectedResume.url} resumeFilename={selectedResume.file_name} flowSessionId={flowSessionId} />}
                </div>
            </div>
        </>
    )
}

export default CreateJob
